import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AssetPickerConfigDto } from '../../../../../../../api/src/creative-unit-package/models/customization/customization.dto';
import { Collection, CollectionTemplate, createCollection } from '../../collection/collection.model';
import { AssetService } from '../asset.service';
import { AssetQuery } from '../asset.query';
import { BaseEntityQuery } from '../../base-entity.query';
import { AssetDto } from '../../../../../../../api/src/asset/models/asset.dto';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { CollectionService } from '../../collection/collection.service';
import { SortDirection } from "../../../../../../../api/src/_core/utils/utils.paging";
import { Filter } from "../../filter/filter.model";
import { searchFilter } from "../../data-source/data-source.data";
import { FilterDef } from "../../../../../../../api/src/data-source/models/filter.model";
import { createFormGroup } from "../../filter/filter.utils";
import { unfreeze } from '../../../utils/object.utils';
import { SessionQuery } from "../../session";
import { MatDialog } from "@angular/material/dialog";
import { AssociateBusinessDialogComponent } from "./associate-business-dialog/associate-business-dialog.component";
import { take } from "rxjs";
import { ConfirmDialogComponent } from '../../../../shared/components/confirm-dialog/confirm-dialog.component';

@Component({
	selector: 'app-asset-picker',
	templateUrl: './asset-picker.component.html',
	styleUrls: ['./asset-picker.component.scss']
})
export class AssetPickerComponent implements OnInit, OnChanges {
	protected readonly SortDirection = SortDirection;
	protected readonly Object = Object;

	@Input() config: AssetPickerConfigDto;
	@Input() selectedItems: AssetDto[];
	@Input() collectionId?: string;
	@Input() enableRemoveFromCollection = false;
	@Input() hideDefaultMenuItems = false;
	@Input() sidebarCollapsed = false;
	@Input() hasSidebar = false;
	@Input() canAssociateWithBusiness: boolean = true;
	@Output() selectItem: EventEmitter<AssetDto> = new EventEmitter<AssetDto>();
	@Output() removeFromCollection: EventEmitter<AssetDto> = new EventEmitter<AssetDto>();
	@Output() setFocalArea: EventEmitter<AssetDto> = new EventEmitter<AssetDto>();

	public activePreviewId: string;
	public menuAsset: AssetDto | null = null;

	public collection: Collection;
	public template: CollectionTemplate;

	public filterForm: UntypedFormGroup;
	public filters: FilterDef[] = [];

	public isSuperAdmin: boolean = false;

	private filterFormSubscription: any;

	readonly sortTranslations: {[key: string]: string} = {
		'Date': $localize`:Sort By@@assetPickerSortCreatedAt:Date`,
		'Title': $localize`:Sort By@@assetPickerSortTitle:Title`
	}

	readonly sortDirectionTranslations: {[key: string]: string} = {
		'ASC': $localize`:Ascending@@assetPickerSortDirectionAsc:ASC`,
		'DESC': $localize`:Descending@@assetPickerSortDirectionDesc:DESC`
	}

	/**
	 * Calculate grid layout based on sidebar state
	 * - No sidebar: Use default from config (usually grid-5)
	 * - Sidebar expanded (250px): Use grid-4 for better spacing
	 * - Sidebar collapsed (90px): Use grid-5 for more content
	 */
	get gridLayoutClass(): string {
		// If no sidebar, use config default
		if (!this.hasSidebar) {
			return this.config?.ui?.layout?.id || 'grid-5';
		}

		// Adjust grid based on sidebar state
		if (this.sidebarCollapsed) {
			return 'grid-5'; // More columns when sidebar is collapsed
		} else {
			return 'grid-4'; // Fewer columns when sidebar is expanded
		}
	}

	constructor(
		private readonly assetService: AssetService,
		private readonly assetQuery: AssetQuery,
		private readonly sessionQuery: SessionQuery,
		private readonly collectionService: CollectionService,
		private readonly fb: UntypedFormBuilder,
		private readonly dialog: MatDialog
	) {
		this.isSuperAdmin = this.sessionQuery.isAtLeastSuperAdmin();
	}

	ngOnChanges(changes: SimpleChanges): void {
		// Detect config changes (especially datasource changes)
		if (changes['config'] && !changes['config'].firstChange) {
			// Config changed after initialization - recreate collection
			this.initializeCollection();
		}
	}

	ngOnInit(): void {
		this.initializeCollection();
	}

	private initializeCollection(): void {
		// Clean up existing subscription
		if (this.filterFormSubscription) {
			this.filterFormSubscription.unsubscribe();
		}

		let datasource = this.config?.datasource;

		if (!datasource) {
			throw new Error('AssetPickerComponent requires a datasource configuration.');
		}

		// Create a mutable copy if the datasource is frozen
		if (Object.isFrozen(datasource)) {
			datasource = { ...datasource, config: datasource.config ? { ...datasource.config } : undefined };
		}

		let config = (datasource.config ?? ({ target: '' } as typeof datasource.config));
		// Create mutable copy of config if it's frozen
		if (Object.isFrozen(config)) {
			config = { ...config };
		}
		if (!datasource.config) {
			datasource.config = config;
		}
		config.filters = config.filters || [];
		config.sortableFields = config.sortableFields || [];

		if (typeof datasource.slug === 'string' && datasource.slug.startsWith('collection-')) {
			const slugId = datasource.slug.replace('collection-', '');
			datasource.slug = 'collection';
			config.target = config.target || slugId;
		}

		if (datasource.slug === 'collection' && !config.target) {
			config.target = this.collectionId;
		}

		if (datasource.slug === 'collection' && !config.target) {
			throw new Error('AssetPickerComponent requires a collection id when using a collection datasource.');
		}

		this.template = {
			id: 'asset-grid',
			slug: 'grid',
			name: 'Grid',
			settings: {
				defaultLimit: 20,
				limitOptions: [20, 40, 60]
			}
		};

		this.collection = createCollection({
			service: this.assetService as any,
			query: this.assetQuery as BaseEntityQuery,
			layoutLocationCode: 'ASSET_PICKER',
			templates: [this.template],
			currentTemplate: this.template,
			methodArgs: [datasource.slug, config.target],
			appendEntities: false,
			paginationMode: 'more',
			queryParams: {
				...(config.params || {}),
				...(this.config.queryParams || {}),
				...(config.sortableFields?.[0]?.path ? { sort: config.sortableFields[0].path } : {}),
				mediaType: this.config.mediaType
			}
		});

		this.filters = config.filters || [];

		this.filters = this.initSearch(this.filters);
		this.filterForm = this.fb.group(createFormGroup(this.filters, this.config.queryParams || {}));
		this.initSort();

		this.filterFormSubscription = this.filterForm.valueChanges.subscribe((value) => {
			this.collectionService.update(this.collection.id, {appendEntities: false, loaded: false});
			this.collectionService.updateQueryParams(this.collection.id, {...value, page: 1});
			this.activePreviewId = null;
		});
	}

	openCollectionMenu(event: MouseEvent, asset: AssetDto) {
		event.stopPropagation();
		if (!asset) {
			return;
		}
		this.menuAsset = asset;
	}

	onMenuClosed(): void {
		this.menuAsset = null;
	}

	onOpenMenu(event: MouseEvent, asset: AssetDto): void {
		event.stopPropagation();
		this.menuAsset = asset;
	}

	openFocalAreaDialog(asset?: AssetDto) {
		const targetAsset = asset || this.menuAsset;
		if (!targetAsset) return;
		this.setFocalArea.emit(targetAsset);
		this.menuAsset = null;
		this.menuAsset = asset;
	}

	canShowDelete(asset?: AssetDto | null): boolean {
		if (!asset) {
			return false;
		}
		if (this.isSuperAdmin) {
			return true;
		}
		if (asset.businessId) {
			if (this.sessionQuery.isOwnerOfBusiness(asset.businessId) || this.sessionQuery.isBusinessAdmin(asset.businessId)) {
				return true;
			}
		}
		return false;
	}

	confirmDelete(asset?: AssetDto) {
		this.dialog
			.open(ConfirmDialogComponent, {
				data: {
					title: $localize`:Delete Asset Dialog@@DeleteAssetDialogTitle:Delete Asset`,
					message: $localize`:Delete Asset Dialog@@DeleteAssetDialogMessage:Deleting this asset may break templates that use it. Continue?`,
					cancel: $localize`:Dialog Button@@dialogActionButtonNoLabel:No`,
					confirm: $localize`:Dialog Button@@dialogActionButtonYesLabel:Yes`
				}
			})
			.afterClosed()
			.pipe(take(1))
			.subscribe(result => {
				if (result) {
					this.assetService.updateAsset(asset.id, { delete: true })
						.pipe(take(1))
						.subscribe(() => {
							this.collectionService.update(this.collection.id, { appendEntities: false, loaded: false });
							this.collectionService.updateQueryParams(this.collection.id, { ...this.collection.queryParams, page: 1 });
						});
				}
			});
	}

	onRemoveFromCollection(event: MouseEvent, asset?: AssetDto) {
		const assetToRemove = asset || this.menuAsset;
		event?.stopPropagation();
		if (!assetToRemove) {
			return;
		}
		this.removeFromCollection.emit(assetToRemove);
		this.menuAsset = null;
	}

	private initSearch(filters: Filter[]) {
		const searchIndex = filters.findIndex(f => f.id === 'search');
		filters = unfreeze(filters);

		switch (this.config.ui.hideSearch) {
			case true:
				if (searchIndex !== -1) {
					filters.splice(searchIndex, 1);
				}
				break;
			default:
				if (searchIndex === -1) {
					filters.push({ ...searchFilter } as Filter);
				}
				break;
		}

		return filters;
	}

	private initSort() {
		if (this.config.datasource.config.sortableFields?.length > 0) {
			this.filterForm.addControl('sort', new UntypedFormControl(this.config.datasource.config.sortableFields?.[0]?.path || ''));
			this.filterForm.addControl('sortStrategy', new UntypedFormControl(SortDirection.DESC));
		}
	}

	localizeSort(sort: string, sortTranslations: { [key: string]: string}): string {
		if(sortTranslations){
			return sortTranslations[sort] || sort;
		} else {
			return sort;
		}

	}

	localizeSortDirection(sort: string, sortDirectionTranslations: { [key: string]: string}): string {
		if(sortDirectionTranslations){
			return sortDirectionTranslations[sort] || sort;
		} else {
			return sort;
		}
	}

	public associateWithBusiness(item) {
		this.dialog.open(AssociateBusinessDialogComponent, {
				width: '600px',
				data: {
					asset: item
				}
			})
			.afterClosed()
			.pipe(take(1))
			.subscribe(result => {
				if (result) {
					this.assetService.updateAsset(item.id, {businessId: result})
						.pipe(take(1))
						.subscribe(_ => {
							this.collectionService.update(this.collection.id, {appendEntities: false, loaded: false});
							this.collectionService.updateQueryParams(this.collection.id, {...this.collection.queryParams, page: 1});
						})
				}
			})
	}
}
